import React, { useState } from "react";
import ReactFlagsSelect from "react-flags-select";
import CircularProgress from "@mui/material/CircularProgress";

import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy, // <== doesn't break if this is rectSortingStrategy
} from "@dnd-kit/sortable";

import "./PredictionForm.css";
import UserInputForm from "../UserInputForm";
import EPLPredictionCard from "./EPLPredictionCard";

function DriversWrapper({ children }) {
  return (
    <div className="prediction-form-enter-predictions-container">
      <div className="prediction-form-enter-here-container">
        <h2 className="prediction-form-enter-predictions-text">
          YOUR PREDICTIONS
        </h2>
        <h2 className="prediction-form-drag-and-drop-text">(DRAG AND DROP)</h2>
      </div>
      <div className="prediction-form-drivers-container">{children}</div>
    </div>
  );
}

const EPLPredictionForm = (props) => {
  const requiredFields = [
    "name",
    "discord",
    "country",
    "mostGoals",
    "mostAssists",
    "firstSackedManager",
    "rankings",
  ];

  const emptyError = { error: false, errorMessage: "", missingFields: [] };

  const formatDriversData = (data) => {
    let d = {};
    for (let i = 0; i < data.length; i++) {
      d[data[i].name] = data[i];
    }
    return d;
  };

  const flattenDriversList = (data) => {
    let l = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].name) {
        l.push(data[i].name);
      }
    }
    return l;
  };

  const getDefaultIds = (data) => {
    let l = [];
    for (let i = 0; i < data.length; i++) {
      l.push(data[i].name);
    }
    return l;
  };

  const [previousForm, setPreviousForm] = useState(false);

  const driverKeys = flattenDriversList(props.data);
  const driversData = formatDriversData(props.data);
  const defaultRankings = getDefaultIds(props.data);

  const [items, setItems] = useState(driverKeys);
  const [selected, setSelected] = useState("");
  const [userData, setUserData] = useState({ rankings: defaultRankings });
  const [formError, setFormError] = useState({ ...emptyError });
  const [submitted, setSubmitted] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const reformatPreviousUserData = (data) => {
    let newUserData = {
      name: data.name,
      discord: data.discord,
      country: data.country,
      dnf: data.dnf,
      overtake: data.overtake,
      rankings: data.rankings,
    };
    return newUserData;
  };

  const updateUserData = (k, v) => {
    let curr = userData;
    curr[k] = v;
    setUserData(curr);
  };

  const updateMostGoals = (e) => {
    updateUserData("mostGoals", e.target.value);
  };

  const updateMostAssists = (e) => {
    updateUserData("mostAssists", e.target.value);
  };

  const updateFirstSackedManager = (e) => {
    updateUserData("firstSackedManager", e.target.value);
  };

  const updateName = (e) => {
    updateUserData("name", e.target.value);
  };

  const updateDiscord = (e) => {
    updateUserData("discord", e.target.value);
  };

  const modifyHandler = (e) => {
    const discordId = prompt(
      "Enter your Discord ID to get your last submission."
    );
    const resp = fetch(
      `https://3vi746w5ca.execute-api.us-east-1.amazonaws.com/default/predictions/${encodeURIComponent(
        discordId
      )}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "OANFQtauHk8HwoXbJZQMD688mD0P0A7I9QeksrI9",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.discord && data.name) {
          setPreviousForm(true);
          let prevData = reformatPreviousUserData(data);
          setUserData(prevData);
          setItems(data.rankings);
          setSelected(prevData.country);
        } else {
          alert(
            "Couldn't find previously entered predictions with that Discord name."
          );
        }
      })
      .catch((error) => {
        alert("Sorry, there was an unknown error.");
      });
  };

  const submitHandler = (e) => {
    setFormError(emptyError);
    let curr = { ...formError };
    let l = [];
    for (let i = 0; i < requiredFields.length; i++) {
      if (userData[requiredFields[i]] == null) {
        l.push(requiredFields[i]);
      }
    }

    if (l.length > 0) {
      setFormError({
        error: true,
        errorMessage: "Missing the following fields:",
        missingFields: l,
      });
      return;
    }

    setSubmitLoading(true);
    const resp = fetch(
      "https://3vi746w5ca.execute-api.us-east-1.amazonaws.com/default/epl/predictions",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "OANFQtauHk8HwoXbJZQMD688mD0P0A7I9QeksrI9",
        },
        body: JSON.stringify(userData),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.discord && data.name) {
          setSubmitted(true);
        } else {
          handleApiError("There was an error submitting the form");
        }
      })
      .catch((error) => {
        handleApiError("There was an error submitting the form");
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  return (
    <div className="prediction-form-container">
      <div className="prediction-form-user-info-container">
        <h2 className="prediction-form-enter-predictions-text">ENTER HERE</h2>
        <div className="prediction-form">
          <UserInputForm
            prevVal={userData.name}
            callbackFunc={updateName}
            inputName={"Name"}
          />
          <UserInputForm
            prevVal={userData.discord}
            callbackFunc={updateDiscord}
            inputName={"Discord (@name)"}
          />
          <ReactFlagsSelect
            selectButtonClassName="prediction-form-country-select"
            placeholder="Country"
            selected={selected}
            searchable
            onSelect={(code) => {
              setSelected(code);
              updateUserData("country", code);
            }}
          />
          <UserInputForm
            prevVal={userData.mostGoals}
            callbackFunc={updateMostGoals}
            inputName={"Most Goals (Player)"}
          />
          <UserInputForm
            prevVal={userData.mostAssists}
            callbackFunc={updateMostAssists}
            inputName={"Most Assists (Player)"}
          />
          <UserInputForm
            prevVal={userData.firstSackedManager}
            callbackFunc={updateFirstSackedManager}
            inputName={"First Manager to be Sacked"}
          />

          <div className="prediction-form-buttons-container">
            {/* {!previousForm && (
              <div
                className="prediction-form-submit-button-container"
                onClick={modifyHandler}
              >
                <p className="prediction-form-submit-button-text">Modify</p>
              </div>
            )} */}
            <div
              className="prediction-form-submit-button-container"
              onClick={submitHandler}
            >
              <p className="prediction-form-submit-button-text">Submit</p>
            </div>
            {submitLoading && <CircularProgress />}
          </div>
        </div>
        {formError.error && (
          <div className="prediction-form-submit-error-container">
            <p>{formError.errorMessage}</p>
            {formError.missingFields.map((rzn, idx) => (
              <p key={idx} className="prediction-form-submit-error-text">
                {rzn}
              </p>
            ))}
          </div>
        )}
        {submitted && (
          <p className="prediction-form-submitted-text">Submitted!</p>
        )}
      </div>
      <DndContext
        autoScroll={false}
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <DriversWrapper>
            {items.map((name, index) => (
              <div
                key={index + 21}
                className="prediction-form-user-selected-slot"
              >
                <div className="prediction-form-user-selected-pos-container">
                  <p
                    key={index + 41}
                    className="prediction-form-user-selected-pos-text"
                  >
                    {index + 1}
                  </p>
                </div>
                <EPLPredictionCard
                  data={driversData[name]}
                  key={name}
                  name={name}
                  index={index}
                />
              </div>
            ))}
          </DriversWrapper>
        </SortableContext>
      </DndContext>
    </div>
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems([...newItems]);
      let rankings = [];
      for (let i = 0; i < items.length; i++) {
        rankings.push(driversData[newItems[i]].name);
      }
      updateUserData("rankings", rankings);
    }
  }

  function handleApiError(msg) {
    let curr = { ...formError };
    curr.error = true;
    curr.errorMessage = msg;
    setFormError(curr);
  }
};

export default EPLPredictionForm;
